import { useEffect } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
} from 'lucide-react';
import { useToasts, type Toast, type ToastTone } from '@/store/toasts';
import { cn } from '@/lib/utils';

const TONE_ICON: Record<ToastTone, typeof CheckCircle2> = {
  good: CheckCircle2,
  warn: AlertTriangle,
  critical: AlertOctagon,
  info: Info,
};

const TONE_RING: Record<ToastTone, string> = {
  good: 'border-status-good/40 text-status-good',
  warn: 'border-status-warn/40 text-status-warn',
  critical: 'border-status-critical/40 text-status-critical',
  info: 'border-accent/40 text-accent',
};

/**
 * Generic application toasts — distinct from `AlertHost`, which is tied to
 * RobotEvent severity. Stack sits below the AlertHost toasts so warning/critical
 * fleet events stay visually dominant.
 */
export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  useEffect(() => {
    const timers: number[] = [];
    for (const t of toasts) {
      if (t.durationMs > 0) {
        timers.push(window.setTimeout(() => dismiss(t.id), t.durationMs));
      }
    }
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[55] flex w-[calc(100vw-2rem)] max-w-[360px] flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Icon = TONE_ICON[toast.tone];
  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-2 rounded-lg border bg-surface-2/95 p-3 shadow-2xl backdrop-blur-md',
        TONE_RING[toast.tone],
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold">{toast.title}</p>
        {toast.body && (
          <p className="mt-0.5 text-xs text-fg-secondary">{toast.body}</p>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-fg-muted hover:text-fg-primary"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
